// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT ACTION MODAL — gestion des notes (lot B.9 — découpage du monstre)
// Migration S4.1 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Trois cas distincts :
//   - new_action : `_wireNewActionNotes(pendingNotes)` — l'action n'existe
//     pas encore en BDD, les notes sont accumulées dans un tableau in-memory
//     et envoyées au backend lors du save.
//   - edit_action : `_wireAddNoteButton(actionId)` + `_loadNotes(actionId)`
//     — l'action existe, on parle directement à l'API (POST/PATCH/DELETE).
//
// Bare refs utilisées (résolues via core-globals.d.ts) :
//   esc, t, toast, currentUserId, openModal, closeModal + api

interface NoteFromApi {
  id: number;
  comment: string;
  user_id?: number | null;
  author_name?: string | null;
  created_at?: string | null;
}

export function _wireNewActionNotes(pendingNotes: string[]): void {
  const renderPending = (): void => {
    const list = document.getElementById('notes-list');
    if (!list) return;
    if (pendingNotes.length === 0) {
      list.innerHTML = `<p style="color:var(--text3);font-size:12px;padding:4px 0">${esc(t('actionModal.notes.empty'))}</p>`;
      return;
    }
    list.innerHTML = pendingNotes
      .slice()
      .reverse()
      .map((n, rIdx) => {
        const idx = pendingNotes.length - 1 - rIdx;
        return `<div style="background:var(--accent-bg);border:1px solid var(--border);border-radius:var(--radius);padding:8px 10px;margin-bottom:6px;display:flex;align-items:flex-start;gap:8px">
        <div style="flex:1;font-size:13px;white-space:pre-wrap">${esc(n)}</div>
        <button class="btn btn-danger btn-icon pending-note-del" data-idx="${idx}" style="font-size:10px">✕</button>
      </div>`;
      })
      .join('');
    list.querySelectorAll<HTMLButtonElement>('.pending-note-del').forEach((btn) => {
      btn.addEventListener('click', () => {
        pendingNotes.splice(parseInt(btn.dataset['idx'] || '0'), 1);
        renderPending();
      });
    });
  };
  const inp = document.getElementById('new-note-text') as HTMLTextAreaElement | null;
  const btn = document.getElementById('btn-add-note');
  const doAdd = (): void => {
    const txt = inp?.value.trim();
    if (!txt) return;
    pendingNotes.push(txt);
    if (inp) {
      inp.value = '';
      // Mai 2026 : signale à l'éditeur (cf. addActionNote en mode édition)
      // pour que `_userEditedNote` soit relâché et que les auto-notes
      // suivantes repeuplent la textarea.
      inp.dispatchEvent(new CustomEvent('ea-note-added', { bubbles: true }));
    }
    inp?.focus();
    renderPending();
  };
  inp?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      doAdd();
    }
  });
  btn?.addEventListener('click', doAdd);
  renderPending();
}

// Bind du bouton "Ajouter note" — appelé UNE FOIS à l'ouverture de la modale.
// Anciennement bindé dans _loadNotes (qui est ré-appelé après chaque add/edit/
// delete) → chaque rebind ajoutait un nouveau listener sans retirer l'ancien,
// d'où le doublon de note signalé. Fix avril 2026.
export function _wireAddNoteButton(actionId: number): void {
  const addBtn = document.getElementById('btn-add-note') as HTMLButtonElement | null;
  const noteInp = document.getElementById('new-note-text') as HTMLTextAreaElement | null;
  if (!addBtn || !noteInp) return;
  const doAdd = async (): Promise<void> => {
    const txt = noteInp.value.trim();
    if (!txt) {
      toast(t('actionModal.notes.commentRequired'), 'error');
      return;
    }
    await api.addActionNote(actionId, { comment: txt, user_id: currentUserId() });
    noteInp.value = '';
    // Mai 2026 : signale à l'éditeur que la textarea a été vidée par le "+"
    // pour qu'il puisse réinitialiser `_userEditedNote` et reprendre les
    // suggestions auto-note sur les prochaines modifications de champs.
    noteInp.dispatchEvent(new CustomEvent('ea-note-added', { bubbles: true }));
    _loadNotes(actionId);
    toast(t('actionModal.notes.added'));
  };
  addBtn.addEventListener('click', () => {
    void api.withButton(addBtn, doAdd);
  });
  noteInp.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void doAdd();
    }
  });
}

export function _loadNotes(actionId: number): void {
  const container = document.getElementById('notes-list');
  if (!container) return;
  container.innerHTML = `<div style="color:var(--text3);font-size:12px;padding:4px 0">${esc(t('actionModal.notes.loading'))}</div>`;

  api
    .getActionNotes(actionId)
    .then((rawNotes) => {
      const notes = rawNotes as NoteFromApi[];
      const badge = document.getElementById('notes-badge');
      if (badge) badge.textContent = notes.length > 0 ? String(notes.length) : '';
      // Mai 2026 : tri chronologique ascendant (ancien en haut, récent en bas)
      // pour permettre un scroll-to-bottom révélant la dernière entrée — la
      // plus pertinente pour reprendre le fil d'une action.
      const sorted = [...notes].sort((a, b) =>
        (a.created_at || '').localeCompare(b.created_at || ''),
      );
      // Format date FR (RefactoUX/Actions 2026-05-20) : DD/MM/YYYY HH:MM en
      // local navigateur. Le backend renvoie de l'UTC sans suffixe `Z`
      // (convention SQLAlchemy / FastAPI default) — on l'ajoute avant
      // `new Date()` pour éviter une interprétation en "naive local".
      const fmtNoteDate = (raw: string | null | undefined): string => {
        if (!raw) return '';
        const iso = /[zZ]|[+-]\d{2}:\d{2}$/.test(raw) ? raw : raw + 'Z';
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return raw;
        return d.toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      };
      container.innerHTML =
        sorted.length > 0
          ? sorted
              .map((n) => {
                const isOwn = n.user_id === currentUserId();
                const dt = fmtNoteDate(n.created_at);
                return `<div class="note-item" data-nid="${n.id}"
            style="background:${isOwn ? 'var(--accent-bg)' : 'var(--bg2)'};border:1px solid var(--border);
                   border-radius:var(--radius);padding:8px 10px;margin-bottom:6px">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:4px">
              <span style="font-size:11px;font-weight:500;color:var(--text2)">${esc(n.author_name || '—')} · ${esc(dt)}</span>
              <div style="display:flex;gap:4px">
                ${
                  isOwn
                    ? `<button class="note-edit-btn btn btn-secondary btn-icon" data-nid="${n.id}" style="font-size:10px;padding:1px 5px">✎</button>
                           <button class="note-del-btn btn btn-danger btn-icon" data-nid="${n.id}" style="font-size:10px;padding:1px 5px">✕</button>`
                    : ''
                }
              </div>
            </div>
            <div class="note-body" style="font-size:13px;color:var(--text);white-space:pre-wrap">${esc(n.comment)}</div>
          </div>`;
              })
              .join('')
          : `<p style="color:var(--text3);font-size:12px;padding:4px 0">${esc(t('actionModal.notes.empty'))}</p>`;

      container.querySelectorAll<HTMLButtonElement>('.note-edit-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const nid = parseInt(btn.dataset['nid'] || '0');
          const item = container.querySelector<HTMLElement>(`.note-item[data-nid="${nid}"]`);
          const body = item?.querySelector<HTMLElement>('.note-body');
          if (!body) return;
          const orig = notes.find((n) => n.id === nid)?.comment || '';
          const ta = document.createElement('textarea');
          ta.value = orig;
          ta.rows = 2;
          ta.style.cssText = 'width:100%;font-size:13px;margin-top:4px;resize:vertical';
          body.replaceWith(ta);
          btn.textContent = '✔';
          btn.addEventListener(
            'click',
            () => {
              void (async () => {
                if (!ta.value.trim()) {
                  toast(t('actionModal.notes.editRequired'), 'error');
                  return;
                }
                await api.updateActionNote(actionId, nid, {
                  comment: ta.value.trim(),
                  user_id: currentUserId(),
                });
                _loadNotes(actionId);
              })();
            },
            { once: true },
          );
        });
      });
      container.querySelectorAll<HTMLButtonElement>('.note-del-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          openModal(
            t('actionModal.notes.deleteTitle'),
            t('actionModal.notes.deleteBody'),
            async () => {
              await api.deleteActionNote(actionId, parseInt(btn.dataset['nid'] || '0'));
              closeModal();
              _loadNotes(actionId);
            },
            t('common.delete'),
          );
        });
      });
      // Mai 2026 : scroll smooth tout en bas pour révéler la note la plus
      // récente. Tri ancien→récent ci-dessus → la dernière entrée est en bas.
      // requestAnimationFrame : laisse le navigateur calculer scrollHeight
      // sur le DOM fraîchement injecté.
      requestAnimationFrame(() => {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    })
    .catch(() => {
      container.innerHTML = `<p style="color:var(--danger);font-size:12px">${esc(t('actionModal.notes.loadError'))}</p>`;
    });

  // Note : le binding du bouton "Ajouter note" est désormais dans
  // _wireAddNoteButton(), appelé une seule fois à l'ouverture de la modale.
}
